import React, { ReactElement } from 'react';
import { Image } from '../../../commonTypes/image';
import { CallToAction } from '../../../commonTypes/links';
import { VideoType } from '../../../commonTypes/video';
import componentProps, { ComponentProps } from '../../../helpers/componentProps';
import { applyComponentTheme } from '../../../theme';
import { GridItem } from '../../Grid';
import { SquareVideo } from '../../Video';
import {
  Body,
  Container,
  CtasContainer,
  Heading,
  MainImage,
  MediaContainer,
  MediaWrapper,
  OptionalCaption,
  ReversibleGrid,
  StyledGridItem,
  StyledPrimaryCta,
  StyledSecondaryCta,
  Subhead,
  TextContent,
  TriangleContainer,
} from './styles';

export type HeroWithMediaProps = {
  heading: string;
  subhead?: string;
  body?: string;
  image?: Image;
  video?: VideoType;
  primaryCta?: CallToAction;
  secondaryCta?: CallToAction;
  caption?: string;
  hideTriangle?: boolean;
  CookieNoticeComponent?: React.ComponentType;
} & ComponentProps;

const HeroWithMedia: React.FC<HeroWithMediaProps> = ({
  heading,
  subhead,
  body,
  image,
  video,
  primaryCta,
  secondaryCta,
  caption,
  hideTriangle = false,
  CookieNoticeComponent,
  ...props
}) => {
  const hasMedia = !!image || !!video;
  const hasCtas = !!primaryCta || !!secondaryCta;

  const Media = (
    img: Image | undefined,
    vid: VideoType | undefined
  ): ReactElement | undefined => {
    if (img) {
      return (
        <MediaWrapper>
          <MainImage image={img} shouldEagerLoad />
          {caption && <OptionalCaption>{caption}</OptionalCaption>}
        </MediaWrapper>
      );
    } else if (vid) {
      return (
        <MediaWrapper>
          <SquareVideo
            src={vid.src}
            screenReaderText={vid.screenReaderText}
            videoCookiePolicyData={vid.videoCookiePolicyData}
          />
          {caption && <OptionalCaption>{caption}</OptionalCaption>}
        </MediaWrapper>
      );
    } else {
      return undefined;
    }
  };

  return (
    <Container>
      <ReversibleGrid {...componentProps(props)} hasMedia={hasMedia}>
        <StyledGridItem
          desktop={hasMedia ? 6 : 8}
          tabletLandscape={hasMedia ? 6 : 8}
          tabletPortrait={8}>
          <TextContent hasCtas={hasCtas} tabIndex={0}>
            <Heading hasMedia={hasMedia}>{heading}</Heading>
            {subhead && <Subhead>{subhead}</Subhead>}
            {body && <Body html={body}></Body>}
          </TextContent>
          {hasCtas && (
            <CtasContainer noOfSections={1}>
              {primaryCta && <StyledPrimaryCta cta={primaryCta} />}
              {secondaryCta && <StyledSecondaryCta cta={secondaryCta} />}
            </CtasContainer>
          )}
        </StyledGridItem>
        <GridItem
          desktop={hasMedia ? 6 : 4}
          tabletLandscape={hasMedia ? 6 : 4}
          tabletPortrait={8}>
          {hasMedia && <MediaContainer>{Media(image, video)}</MediaContainer>}
        </GridItem>
      </ReversibleGrid>
      <TriangleContainer hasMedia={hasMedia} hideTriangle={hideTriangle} />
    </Container>
  );
};

export default applyComponentTheme(HeroWithMedia, 'HeroWithMedia');
